import React, { useState } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Send, Plus, X, Loader2, Mail } from 'lucide-react';

export default function SendInvoiceDialog({ 
  open, 
  onOpenChange, 
  invoice, 
  client,
  onSent 
}) {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [newCcEmail, setNewCcEmail] = useState('');

  if (!invoice) return null;

  const handleAddCc = () => {
    const email = newCcEmail.trim();
    if (email && email.includes('@') && !ccEmails.includes(email)) {
      setCcEmails([...ccEmails, email]);
      setNewCcEmail('');
    }
  };

  const handleRemoveCc = (email) => {
    setCcEmails(ccEmails.filter(e => e !== email));
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await api.sendInvoice(invoice.id, { 
        send_email: true,
        email_message: message || null,
        cc_emails: ccEmails.length > 0 ? ccEmails : null
      });
      const recipientCount = 1 + ccEmails.length;
      toast.success(`Invoice sent to ${recipientCount} recipient${recipientCount > 1 ? 's' : ''}`);
      setMessage('');
      setCcEmails([]);
      onOpenChange(false);
      if (onSent) onSent();
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Get client email from invoice or passed client
  const clientEmail = client?.email || invoice?.client?.email || '';
  const clientName = client?.name || invoice?.client?.name || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Invoice
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Invoice Summary */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium">{invoice.invoice_number}</p>
                <p className="text-sm text-slate-500">{clientName}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold">{formatCurrency(invoice.total)}</p>
                <p className="text-xs text-slate-500">
                  {invoice.balance_due > 0 ? `${formatCurrency(invoice.balance_due)} due` : 'Paid'}
                </p>
              </div>
            </div>
          </div>

          {/* Primary Recipient */}
          <div>
            <Label className="text-sm text-slate-500">Send to</Label>
            <p className="font-medium">{clientEmail}</p>
          </div>

          {/* CC Emails */}
          <div className="space-y-2">
            <Label>CC (Additional Recipients)</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Add email address"
                value={newCcEmail}
                onChange={(e) => setNewCcEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCc())}
              />
              <Button type="button" variant="outline" onClick={handleAddCc}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {ccEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {ccEmails.map((email) => (
                  <span 
                    key={email}
                    className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full text-sm"
                  >
                    {email}
                    <button 
                      type="button"
                      onClick={() => handleRemoveCc(email)}
                      className="hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500">
              Add billing contacts or other stakeholders who should receive this invoice.
            </p>
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label>Message (Optional)</Label>
            <Textarea
              placeholder="Add a personal message to include in the email..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending}>
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Invoice
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
